import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, RotateCcw, Loader2, AlertCircle, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { sitesService, newStocktakeService } from '../services';

// Format ISO date "2026-09-25" → "25 Sep 2026"
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Format number as £1,234.56
function fmtGBP(n) {
  if (typeof n !== 'number') return null;
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Wave 5A P4 — "Start a new stocktake". Reuses the existing site-level
// carry-forward engine (opening_stock_service.populate_opening_stock) and
// the existing session-creation path — this page only sequences the user
// through: pick site -> preview -> create -> land in the normal session.
export default function StartNewStocktakePage() {
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [siteId, setSiteId] = useState('');
  const [siteSearch, setSiteSearch] = useState('');

  // Filter + case-insensitive sort in the UI as a belt-and-suspenders guard
  // (backend already returns lower-cased sorted; this makes typing feel instant).
  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase();
    const list = q ? sites.filter(s => s.name.toLowerCase().includes(q)) : sites;
    return [...list].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [sites, siteSearch]);

  const [step, setStep] = useState('pick-site'); // pick-site | review | creating
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState(null);

  const [idempotencyKey] = useState(() => (
    window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`
  ));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Wave 5A UAT fix: exclude test/fixture sites (e.g. "— DO NOT USE") from the
        // ordinary user site selector. The fixture DB records are preserved intact.
        const data = await sitesService.list({ is_active: true, exclude_do_not_use: true });
        if (active) setSites(Array.isArray(data) ? data : (data?.items || []));
      } catch (e) {
        if (active) toast.error('Could not load sites. Please try again.');
      } finally {
        if (active) setSitesLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleContinue = async () => {
    if (!siteId) return;
    setStep('review');
    setPreflightLoading(true);
    setPreflightError(null);
    try {
      const data = await newStocktakeService.preflight(siteId);
      setPreflight(data);
    } catch (e) {
      setPreflightError(e?.response?.data?.detail || 'Could not check this site right now. Please try again.');
    } finally {
      setPreflightLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const data = await newStocktakeService.create(siteId, idempotencyKey);
      navigate(`/stocktaking/sessions/${data.session.id}`);
    } catch (e) {
      setCreateError(e?.response?.data?.detail || 'Could not create the stocktake. Please try again.');
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="start-new-stocktake-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => (step === 'pick-site' ? navigate('/stocktaking/import') : setStep('pick-site'))}
          data-testid="start-new-stocktake-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Start a new stocktake</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Use a previous closing stock as the opening stock for a new editable session
          </p>
        </div>
      </div>

      {step === 'pick-site' && (
        <Card className="max-w-lg" data-testid="start-new-stocktake-pick-site-card">
          <CardHeader>
            <CardTitle className="text-lg">Choose a site</CardTitle>
            <CardDescription>Pick the site you want to start a new stocktake for.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search filter — mirrors Route B's site combobox behaviour */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search sites…"
                value={siteSearch}
                onChange={e => setSiteSearch(e.target.value)}
                className="pl-8 h-9"
                disabled={sitesLoading}
                data-testid="start-new-stocktake-site-search"
              />
            </div>
            <Select value={siteId} onValueChange={setSiteId} disabled={sitesLoading}>
              <SelectTrigger data-testid="start-new-stocktake-site-select">
                <SelectValue placeholder={sitesLoading ? 'Loading sites…' : filteredSites.length === 0 ? 'No sites match' : 'Select a site'} />
              </SelectTrigger>
              <SelectContent>
                {filteredSites.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="w-full"
              disabled={!siteId || sitesLoading}
              onClick={handleContinue}
              data-testid="start-new-stocktake-continue-button"
            >
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 'review' && (
        <Card className="max-w-lg" data-testid="start-new-stocktake-review-card">
          <CardHeader>
            <div className="bg-emerald-600 w-12 h-12 rounded-lg flex items-center justify-center mb-2">
              <RotateCcw className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-lg">Review before you start</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {preflightLoading && (
              <div className="flex items-center gap-2 text-muted-foreground" data-testid="start-new-stocktake-preflight-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking this site's previous stocktake…
              </div>
            )}

            {!preflightLoading && preflightError && (
              <Alert variant="destructive" data-testid="start-new-stocktake-preflight-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{preflightError}</AlertDescription>
              </Alert>
            )}

            {!preflightLoading && !preflightError && preflight && (
              preflight.has_prior_session ? (
                <Alert className="border-emerald-200 bg-emerald-50" data-testid="start-new-stocktake-preview-found">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="space-y-2">
                    <p>
                      The closing stock from{' '}
                      <strong data-testid="start-new-stocktake-source-ref">{preflight.source_reference}</strong>
                      {preflight.source_session_date ? (
                        <> (<span data-testid="start-new-stocktake-source-date">{fmtDate(preflight.source_session_date)}</span>)</>
                      ) : ''}{' '}
                      will become the opening stock for your new stocktake.
                    </p>
                    <ul className="mt-1 space-y-0.5 text-sm">
                      <li data-testid="start-new-stocktake-line-count">
                        <strong>{preflight.line_count}</strong> product line{preflight.line_count === 1 ? '' : 's'} will carry forward
                        {preflight.zero_qty_lines > 0 && (
                          <span className="text-muted-foreground ml-1" data-testid="start-new-stocktake-qty-breakdown">
                            ({preflight.positive_qty_lines} with stock; {preflight.zero_qty_lines} counted as zero)
                          </span>
                        )}
                      </li>
                      {preflight.total_closing_value != null && (
                        <li data-testid="start-new-stocktake-closing-value">
                          Closing stock value:{' '}
                          <strong>{fmtGBP(preflight.total_closing_value)}</strong>
                        </li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert data-testid="start-new-stocktake-preview-empty">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No previous stocktake was found for this site yet. Your new stocktake will start with
                    blank opening stock, and you can enter it manually.
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <span className="text-muted-foreground">Have a legacy PSB file? </span>
                      <Link
                        to={`/stocktaking/import/historical-stocktake?mode=route-a&site_id=${siteId}`}
                        className="underline font-medium text-slate-700 hover:text-slate-900"
                        data-testid="start-new-stocktake-psb-link"
                      >
                        Import closing stock from it instead
                      </Link>
                      <span className="text-muted-foreground"> — it will become the opening stock for this site.</span>
                    </div>
                  </AlertDescription>
                </Alert>
              )
            )}

            {createError && (
              <Alert variant="destructive" data-testid="start-new-stocktake-create-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}

            <Button
              className="w-full"
              disabled={preflightLoading || !!preflightError || creating}
              onClick={handleCreate}
              data-testid="start-new-stocktake-create-button"
            >
              {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>) : 'Create stocktake'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
